// Wave 20.34 — "Ride comfort" score.
//
// aero.chat's unique angle on the weather forecast: rather than dumping
// numbers, we synthesize a 0–10 score of how good a given hour is for
// riding or running outside. Deliberately tuned to a "sport-fit" adult
// on a road bike or run — not sitting on a patio, not climbing Everest.
//
// Inputs (any missing = neutral / not-penalized for that dimension):
//   temperature_c, wind_speed_ms, humidity_pct, precip_mm_1h
//
// Weights derived from cycling coach anecdote + reading around:
//   - ~18°C (64°F) is the sweet spot; every degree away costs
//     progressively more (heat > cold at same delta).
//   - Wind up to ~3 m/s (7 mph) is fine; beyond that each m/s knocks
//     ~0.35 off. Not a killer, just harder work.
//   - Humidity < 65% is a wash; above that we start subtracting.
//   - Rain > 0.1 mm/h is a real -3 penalty (visibility + traction),
//     scaling further with intensity.

import type { UnitSystem } from './useUnitSystem';

export interface ComfortInput {
  temperature_c: number | null;
  wind_speed_ms: number | null;
  humidity_pct: number | null;
  precip_mm_1h: number | null;
}

export interface ComfortBreakdown {
  score: number;             // 0..10
  tempPenalty: number;
  windPenalty: number;
  humidityPenalty: number;
  precipPenalty: number;
  primaryReason: string;     // one-liner "warm 22°C, 8 mph SW wind, dry"
}

const IDEAL_TEMP_C = 18;

export function computeComfort(f: ComfortInput, u: UnitSystem = 'metric'): ComfortBreakdown {
  let score = 10;
  let tempPenalty = 0;
  let windPenalty = 0;
  let humidityPenalty = 0;
  let precipPenalty = 0;

  if (f.temperature_c != null) {
    const dev = f.temperature_c - IDEAL_TEMP_C;
    // Asymmetric: heat hurts more than cold at same absolute delta.
    const magnitude = Math.abs(dev) / (dev > 0 ? 8 : 10);
    tempPenalty = Math.pow(magnitude, 1.5) * 3.5;
  }
  if (f.wind_speed_ms != null && f.wind_speed_ms > 3) {
    windPenalty = (f.wind_speed_ms - 3) * 0.35;
  }
  if (f.humidity_pct != null && f.humidity_pct > 65) {
    humidityPenalty = (f.humidity_pct - 65) * 0.05;
  }
  if (f.precip_mm_1h != null && f.precip_mm_1h > 0.05) {
    precipPenalty = 3 + Math.min(4, f.precip_mm_1h * 2);
  }

  score = Math.max(
    0,
    Math.min(10, 10 - tempPenalty - windPenalty - humidityPenalty - precipPenalty),
  );

  const parts: string[] = [];
  if (f.temperature_c != null) {
    const label =
      f.temperature_c < 5
        ? 'cold'
        : f.temperature_c < 12
        ? 'chilly'
        : f.temperature_c < 22
        ? 'mild'
        : f.temperature_c < 28
        ? 'warm'
        : 'hot';
    const t = u === 'imperial'
      ? `${Math.round((f.temperature_c * 9) / 5 + 32)}°F`
      : `${Math.round(f.temperature_c)}°C`;
    parts.push(`${label} ${t}`);
  }
  if (f.wind_speed_ms != null) {
    const w = f.wind_speed_ms;
    const label = w < 3 ? 'calm' : w < 7 ? 'breezy' : w < 11 ? 'windy' : 'gusty';
    parts.push(label);
  }
  if (f.precip_mm_1h != null && f.precip_mm_1h > 0.05) {
    parts.push('rain');
  } else if ((f.humidity_pct ?? 0) > 80) {
    parts.push('humid');
  }

  return {
    score,
    tempPenalty,
    windPenalty,
    humidityPenalty,
    precipPenalty,
    primaryReason: parts.join(' · '),
  };
}

/** Traffic-light color for a score. */
export function comfortColor(score: number): string {
  if (score >= 7.5) return '#3ddc84';     // green
  if (score >= 5.5) return '#e8c547';     // yellow
  if (score >= 3.5) return '#e8823f';     // orange
  return '#e05252';                        // red
}

/** Fun, contextual headline that references the DOMINANT reason the
 *  score isn't higher. Activity-agnostic on purpose — no "ride" or
 *  "run" so it works for whatever we add next. Rotates a small pool
 *  of "send it" messages for perfect windows so it doesn't feel
 *  templated when a user comes back an hour later. */
export function comfortHeadline(
  f: ComfortInput,
  breakdown: ComfortBreakdown,
): string {
  const score = breakdown.score;

  // Perfect / near-perfect windows — no factor to warn about.
  if (score >= 8.5) {
    const pool = ['Send it', 'Get out there', 'Prime window', 'Green light — go'];
    return pool[Math.floor(Math.abs(Math.sin((f.temperature_c ?? 0) * 13)) * pool.length) % pool.length];
  }

  const factors = [
    { kind: 'precip' as const, penalty: breakdown.precipPenalty },
    { kind: 'temp'   as const, penalty: breakdown.tempPenalty },
    { kind: 'wind'   as const, penalty: breakdown.windPenalty },
    { kind: 'humid'  as const, penalty: breakdown.humidityPenalty },
  ].sort((a, b) => b.penalty - a.penalty);
  const dom = factors[0];

  // Precip is decisive — call it out no matter how mild.
  if (dom.kind === 'precip' && dom.penalty > 2.5) {
    if (score < 3.5) return 'Inside day — heavy rain';
    return 'Rain — grab a jacket';
  }
  if (dom.kind === 'temp' && dom.penalty > 1.5) {
    const t = f.temperature_c ?? 18;
    if (t > 18) {
      if (score < 4) return 'Baking — early or late only';
      if (t > 26) return 'Hot — hydrate before you go';
      return 'Warm — carry extra water';
    } else {
      if (score < 4) return 'Cold — bundle up hard';
      if (t < 3) return 'Freezing — gloves + windbreaker';
      return 'Chilly — layer up first';
    }
  }
  if (dom.kind === 'wind' && dom.penalty > 1) {
    const speed = f.wind_speed_ms ?? 0;
    if (speed > 12) return 'Gusty — pick a sheltered route';
    if (speed > 8) return 'Windy — plan a loop with the wind';
    return 'Breezy — heads-up on exposed sections';
  }
  if (dom.kind === 'humid' && dom.penalty > 1) {
    return 'Muggy — extra water and easy pace';
  }

  // Middling — no single dominant factor.
  if (score >= 7) return 'Solid window';
  if (score >= 5.5) return 'Doable';
  if (score >= 4) return 'Grind — pick your battles';
  return 'Skip this one';
}

/** Returns an Aero-AI-indoors nudge string when conditions make the
 *  outdoor call unpleasant enough (or when it's dark) that the indoor
 *  aero session is genuinely the better choice. `when` is the wall
 *  time of the forecast slot (parent computes: seriesAnchor + hourIdx).
 *  Returns null when outdoor is fine. */
export function aeroSuggestion(
  f: ComfortInput,
  breakdown: ComfortBreakdown,
  when?: Date | null,
): string | null {
  const rainy = (f.precip_mm_1h ?? 0) > 0.5;
  const hot = (f.temperature_c ?? 18) > 30;
  const freezing = (f.temperature_c ?? 18) < 0;
  const dark = when
    ? when.getHours() >= 20 || when.getHours() < 6
    : false;
  const gnarly = breakdown.score < 4;

  if (rainy) return "Good time for an Aero AI session indoors";
  if (freezing) return "Rough out — Aero AI on the trainer's a better call";
  if (hot) return "Beat the heat — Aero AI indoors";
  if (dark) return "Sun's down — perfect Aero AI window";
  if (gnarly) return "Ugly out — worth an Aero AI session instead";
  return null;
}
